import { TextField, type TextFieldProps } from '@side/pantry';
import { kebabCase } from 'lodash';
import { forwardRef } from 'react';
import { type UseControllerProps, useController } from 'react-hook-form';

interface Props extends Omit<TextFieldProps, 'ref'> {
  name: string;
  rules?: UseControllerProps['rules'] & {
    validate?: (value: string) => boolean | string;
  };
  disableRequiredHighlight?: boolean;
  fixedHeight?: boolean;
}

const FormTextField = forwardRef<HTMLDivElement, Props>(
  (
    {
      label,
      name,
      rules,
      defaultValue,
      type,
      required,
      disableRequiredHighlight,
      error: textFieldError,
      fixedHeight,
      ...rest
    },
    ref,
  ) => {
    const { validate, required: _requiredRule, ...otherRules } = rules ?? {};

    const {
      field,
      fieldState: { error, isTouched },
    } = useController({
      name,
      defaultValue: defaultValue === undefined ? '' : defaultValue,
      rules: {
        required: required ? 'Required' : false,
        validate: (value: string) => {
          const customValidationResult = validate ? validate(value) : true;

          if (typeof customValidationResult === 'string') {
            return customValidationResult;
          }

          return true;
        },
        ...otherRules,
      },
    });

    const id = kebabCase(name);

    return (
      <TextField
        id={id}
        label={label}
        type={type ?? 'text'}
        defaultValue={defaultValue}
        required={required}
        highlight={
          !isTouched && required && !field.value && !disableRequiredHighlight
            ? 'secondary'
            : undefined
        }
        error={(isTouched && !!error) || textFieldError}
        helperText={isTouched && error ? error.message : ''}
        fullWidth
        {...field}
        {...rest}
        slotProps={{
          ...rest.slotProps,
          select: {
            ...rest.slotProps?.select,
            labelId: `${id}-label`,
            MenuProps: {
              slotProps: {
                list: {
                  id: `${id}-menu-list`,
                },
              },
            },
          },
        }}
        onBlur={(e) => {
          field.onBlur();
          rest.onBlur?.(e);
        }}
        onChange={(e) => {
          field.onChange(e.target.value);
          rest.onChange?.(e);
        }}
        ref={ref}
        fixedHeight={fixedHeight}
      />
    );
  },
);

FormTextField.displayName = 'FormTextField';

export { FormTextField };
